import axios from "axios";
import api, { type ApiResponse } from "./api";

export type PublisherRegistrationInput = {
  fullName: string;
  appName: string;
  email: string;
  password: string;
};

export type PublisherRegistrationResult = {
  id: string;
  email: string;
  fullName: string;
  role: "publisher";
};

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const DUPLICATE_EMAIL_REGISTRATION_MESSAGE =
  "An account with this email already exists. Please Log In.";

/** Founder-approved guidance when email is not tied to an eligible publisher account. */
export const PUBLISHER_LOGIN_PRECHECK_MESSAGE =
  "Please create a Publisher Account first.";

/** Returned when credentials are valid but the account is not an eligible publisher. */
export const NON_PUBLISHER_ACCOUNT_LOGIN_MESSAGE = "Publisher account required.";

export function hasValidPublisherLoginEmailFormat(email: string): boolean {
  const normalized = email.trim().toLowerCase();
  return Boolean(normalized) && EMAIL_PATTERN.test(normalized);
}

export function isDuplicateEmailRegistrationError(message: string): boolean {
  return message === DUPLICATE_EMAIL_REGISTRATION_MESSAGE;
}

export function isNonPublisherAccountLoginError(error: unknown): boolean {
  if (!axios.isAxiosError(error)) {
    return false;
  }
  const status = error.response?.status;
  const message =
    typeof error.response?.data?.message === "string"
      ? error.response.data.message
      : "";
  return status === 403 && message === NON_PUBLISHER_ACCOUNT_LOGIN_MESSAGE;
}

export function validatePublisherRegistrationInput(
  input: PublisherRegistrationInput,
): string[] {
  const errors: string[] = [];
  const fullName = input.fullName.trim();
  const appName = input.appName.trim();
  const email = input.email.trim().toLowerCase();
  const password = input.password;

  if (!fullName) {
    errors.push("Full name is required.");
  } else if (fullName.length < 2) {
    errors.push("Full name must be at least 2 characters.");
  } else if (fullName.length > 120) {
    errors.push("Full name must be 120 characters or fewer.");
  }

  if (!appName) {
    errors.push("App name is required.");
  } else if (appName.length > 120) {
    errors.push("App name must be 120 characters or fewer.");
  }

  if (!email) {
    errors.push("Email is required.");
  } else if (!EMAIL_PATTERN.test(email)) {
    errors.push("Email must be a valid email address.");
  }

  if (!password) {
    errors.push("Password is required.");
  } else if (password.length < 8) {
    errors.push("Password must be at least 8 characters.");
  } else if (password.length > 128) {
    errors.push("Password must be 128 characters or fewer.");
  }

  return errors;
}

export function validatePublisherLoginInput(input: {
  email: string;
  password: string;
}): string[] {
  const errors: string[] = [];
  const email = input.email.trim().toLowerCase();

  if (!email) {
    errors.push("Email is required.");
  } else if (!EMAIL_PATTERN.test(email)) {
    errors.push("Email must be a valid email address.");
  }

  if (!input.password) {
    errors.push("Password is required.");
  }

  return errors;
}

export function normalizePublisherRegistrationInput(
  input: PublisherRegistrationInput,
): PublisherRegistrationInput {
  return {
    fullName: input.fullName.trim(),
    appName: input.appName.trim(),
    email: input.email.trim().toLowerCase(),
    password: input.password,
  };
}

export function normalizePublisherLoginInput(input: {
  email: string;
  password: string;
}): { email: string; password: string } {
  return {
    email: input.email.trim().toLowerCase(),
    password: input.password,
  };
}

export function resolveAuthErrorMessage(
  error: unknown,
  context: "register" | "login",
): string {
  if (axios.isAxiosError(error)) {
    const status = error.response?.status;
    const message =
      typeof error.response?.data?.message === "string"
        ? error.response.data.message
        : "";

    if (context === "register" && status === 409) {
      return DUPLICATE_EMAIL_REGISTRATION_MESSAGE;
    }

    if (context === "login") {
      if (status === 401) {
        return "Invalid login credentials.";
      }
      if (status === 403 && message.toLowerCase().includes("suspended")) {
        return "This account is suspended.";
      }
      // Non-publisher accounts are routed by LogInPage; never surface as invalid credentials.
      if (status === 403 && message === NON_PUBLISHER_ACCOUNT_LOGIN_MESSAGE) {
        return "";
      }
    }

    if (status === 400) {
      const apiErrors = error.response?.data?.errors;
      if (Array.isArray(apiErrors) && apiErrors.length > 0) {
        return apiErrors.map(String).join(" ");
      }
      if (message) {
        return message;
      }
    }

    if (message && !message.toLowerCase().includes("stack")) {
      return message;
    }
  }

  return context === "register"
    ? "Registration could not be completed. Please check your details and try again."
    : "Invalid login credentials.";
}

export async function registerPublisher(
  input: PublisherRegistrationInput,
): Promise<PublisherRegistrationResult> {
  const normalized = normalizePublisherRegistrationInput(input);
  const response = await api.post<
    ApiResponse<{ user: PublisherRegistrationResult }>
  >("/auth/register", normalized);

  return response.data.data.user;
}

export async function checkPublisherLoginEligibility(email: string): Promise<boolean> {
  const normalized = email.trim().toLowerCase();
  const response = await api.post<ApiResponse<{ eligible: boolean }>>(
    "/auth/publisher-login-eligibility",
    { email: normalized },
  );
  return response.data.data.eligible;
}
